import { useNavigate } from "react-router-dom";
import { Heart, Star, MapPin, Award } from "lucide-react";
import BadgePill from "@/components/BadgePill";
import { VerifiedBadge } from "@/components/VerifiedBadge";

export default function ProviderCard({ provider, isFavorite, onToggleFavorite }) {
  const navigate = useNavigate();
  const verifiedCerts = (provider.certifications || []).filter((c) => c.reviewed);
  return (
    <div className={`card-metal p-5 flex flex-col gap-3 ${provider.featured_week ? "ring-2 ring-amber-400" : ""}`} data-testid={`provider-card-${provider.id}`}>
      {provider.featured_week && (
        <span className="inline-flex items-center gap-1.5 self-start text-[11px] font-black text-amber-900 bg-gradient-to-r from-amber-200 to-amber-100 border border-amber-400 rounded-full px-2.5 py-1 -mt-1" data-testid={`featured-week-badge-${provider.id}`}>
          <Award className="w-3.5 h-3.5" /> PRO OF THE WEEK
        </span>
      )}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-heading font-black text-lg text-slate-900">{provider.name}</h3>
          <BadgePill tier={provider.badge} testId={`provider-badge-${provider.id}`} />
          {provider.is_veteran && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5 mt-1" data-testid={`veteran-badge-${provider.id}`}>
              <Star className="w-3 h-3 fill-green-600 text-green-600" /> Verified Veteran
            </span>
          )}
          {provider.backup_status && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#9b111e] bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5 mt-1 ml-1" data-testid={`backup-badge-${provider.id}`}>
              Back-Up Pro · {provider.backup_discount_pct || 20}% off
            </span>
          )}
        </div>
        {onToggleFavorite && (
          <button
            data-testid={`favorite-btn-${provider.id}`}
            onClick={() => onToggleFavorite(provider.id)}
            className="p-2 rounded-full hover:bg-red-50 transition-colors"
            aria-label="Save favorite"
          >
            <Heart
              className="w-6 h-6"
              fill={isFavorite ? "#9b111e" : "none"}
              color="#9b111e"
            />
          </button>
        )}
      </div>

      {provider.bio && <p className="text-sm text-slate-600 line-clamp-2">{provider.bio}</p>}

      {(verifiedCerts.length > 0 || (provider.certificates || []).length > 0) && (
        <div className="flex flex-wrap gap-1.5" data-testid={`provider-cert-badges-${provider.id}`}>
          {(provider.certificates || []).map((c) => (
            <VerifiedBadge key={c.id} name={c.name}
              className="text-[11px] font-bold text-[#9b111e] bg-red-50 border border-[#9b111e]/30 rounded-full px-2 py-0.5" />
          ))}
          {verifiedCerts.map((c) => (
            <VerifiedBadge key={c.id} name={c.name} testId={`cert-badge-${c.id}`}
              className="text-[11px] font-bold text-[#9b111e] bg-red-50 border border-[#9b111e]/30 rounded-full px-2 py-0.5" />
          ))}
        </div>
      )}

      <div className="flex items-center gap-4 text-sm text-slate-600">
        <span className="flex items-center gap-1 font-semibold text-[#9b111e]">
          <Star className="w-4 h-4 fill-[#9b111e]" /> {provider.avg_rating || 0}/10
        </span>
        <span>({provider.review_count || 0} reviews)</span>
        {provider.distance != null && (
          <span className="flex items-center gap-1">
            <MapPin className="w-4 h-4" /> {provider.distance} mi
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {(provider.sales || []).some((sl) => new Date(sl.ends_at) > new Date()) && (
          <span className="text-xs font-black text-white bg-[#9b111e] rounded-full px-2 py-0.5" data-testid={`sale-chip-${provider.id}`}>
            🔥 SALE {Math.max(...provider.sales.filter((sl) => new Date(sl.ends_at) > new Date()).map((sl) => sl.pct)).toFixed(0)}% OFF
          </span>
        )}
        {(provider.services || []).slice(0, 4).map((s, i) => (
          <span key={i} className="text-xs bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
            {s.name}
          </span>
        ))}
      </div>

      <button
        data-testid={`view-provider-${provider.id}`}
        onClick={() => navigate(`/provider/${provider.id}`)}
        className="btn-red-3d !py-2 mt-1"
      >
        View & Book
      </button>
    </div>
  );
}
